// THREE import removed - not used
import { SceneManager } from './SceneManager';
import { Renderer } from './Renderer';
import { Camera } from './Camera';
import { Controls } from './Controls';
import { Resizer } from '../utils/Resizer';
import { ScrollController } from '../utils/ScrollController';
import { PostProcessing } from './PostProcessing';
import { FAQAnimation } from '../objects/FAQAnimation';
// import { GSAPTypography } from '../objects/GSAPTypography'; // Removed - hero content section removed
import { PopularPlantsCarousel } from '../objects/PopularPlantsCarousel';
import { PopularPlantsReveal } from '../objects/PopularPlantsReveal';
import { BlogHorizontalScroll } from '../objects/BlogHorizontalScroll';
import { FooterAnimation } from '../objects/FooterAnimation';
import { HeroNavAnimation } from '../objects/HeroNavAnimation';
import { ScrambleLabelsController } from '../objects/ScrambleLabelsController';
import { HeroLiquidBackground } from '../objects/HeroLiquidBackground';
import { HeroBackgroundRotation } from '../objects/HeroBackgroundRotation';
import { PlantScanDialog, PlantDetailPayload } from '../objects/PlantScanDialog';
import { BlogDetailView } from '../objects/BlogDetailView';
import { IntroLottie } from '../objects/IntroLottie';
import { IntroSectionAnimation } from '../objects/IntroSectionAnimation';
import { IdentifySection3D } from '../objects/IdentifySection3D';
import { IdentifySectionReveal } from '../objects/IdentifySectionReveal';
import { HeroContentReveal } from '../objects/HeroContentReveal';
import { ContactSectionReveal } from '../objects/ContactSectionReveal';
import { BlogSectionReveal } from '../objects/BlogSectionReveal';
import { HeroPhoneLottie } from '../objects/HeroPhoneLottie';
import { HeroPhoneInteractions } from '../objects/HeroPhoneInteractions';
import { IdentifyImagePreview } from '../objects/IdentifyImagePreview';
import { CustomCursor } from '../objects/CustomCursor';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export class App {
  private sceneManager: SceneManager;
  private renderer: Renderer;
  private camera: Camera;
  private controls: Controls | null = null;
  private _resizer: Resizer;
  private scrollController: ScrollController;
  private postProcessing: PostProcessing | null = null;
  private faqAnimation: FAQAnimation | null = null;
  private popularPlantsCarousel: PopularPlantsCarousel | null = null;
  private blogHorizontalScroll: BlogHorizontalScroll | null = null;
  private footerAnimation: FooterAnimation | null = null;
  private heroNavAnimation: HeroNavAnimation | null = null;
  private scrambleLabelsController: ScrambleLabelsController | null = null;
  private heroLiquidBackground: HeroLiquidBackground | null = null;
  private heroBackgroundRotation: HeroBackgroundRotation | null = null;
  private plantScanDialog: PlantScanDialog | null = null;
  private blogDetailView: BlogDetailView | null = null;
  private introLottie: IntroLottie | null = null;
  private introSectionAnimation: IntroSectionAnimation | null = null;
  private identifySection3D: IdentifySection3D | null = null;
  private identifySectionReveal: IdentifySectionReveal | null = null;
  private heroContentReveal: HeroContentReveal | null = null;
  private contactSectionReveal: ContactSectionReveal | null = null;
  private blogSectionReveal: BlogSectionReveal | null = null;
  private identifyImagePreview: IdentifyImagePreview | null = null;
  private customCursor: CustomCursor | null = null;
  private heroPhoneLottie: HeroPhoneLottie | null = null;
  private heroPhoneInteractions: HeroPhoneInteractions | null = null;
  private animationId: number | null = null;
  private popularPlantsReveal: PopularPlantsReveal | null = null;
  private scrollTriggerResizeHandler: (() => void) | null = null;
  private scrollTriggerLoadHandler: (() => void) | null = null;
  private plantDetailsRequestHandler: ((e: CustomEvent<PlantDetailPayload>) => void) | null = null;
  private blogDetailClickHandler: ((e: Event) => void) | null = null;

  constructor() {
    // Initialize scroll controller first
    this.scrollController = new ScrollController();
    
    // Initialize core components
    this.sceneManager = new SceneManager(this.scrollController);
    this.renderer = new Renderer();
    this.camera = new Camera();
    this._resizer = new Resizer(
      this.camera.getCamera(),
      this.renderer.getRenderer()
    );
    void this._resizer; // Mark as intentionally used

    // Setup scroll-based camera animation
    this.setupScrollCameraAnimation();
  }

  private setupScrollCameraAnimation(): void {
    this.scrollController.onScroll((progress) => {
      const camera = this.camera.getCamera();
      
      // Animate camera position based on scroll
      const baseZ = 5;
      const baseY = 2;
      
      camera.position.z = baseZ + progress * 3;
      camera.position.y = baseY - progress * 1.5;
      
      // Slight rotation based on scroll
      camera.rotation.z = progress * 0.1;
    });
  }

  public init(): void {
    // Hide Three.js background - we're using Rive animation only
    const container = document.getElementById('canvas-container');
    if (container) {
      container.style.display = 'none';
    }

        // Setup footer animation
        this.setupFooterAnimation();

        // Hero nav morphing animation (GSAP)
        this.heroNavAnimation = new HeroNavAnimation();

        // Hero background with liquid surface effect (WebGL)
        this.heroLiquidBackground = new HeroLiquidBackground();
        this.heroBackgroundRotation = new HeroBackgroundRotation({
          onImageChange: (url) => this.heroLiquidBackground?.setImage(url),
        });

        // Plant scan dialog and hero CTA
        this.plantScanDialog = new PlantScanDialog();
        const heroCtaIdentify = document.getElementById('hero-cta-identify');
        if (heroCtaIdentify && this.plantScanDialog) {
          heroCtaIdentify.addEventListener('click', () => {
            this.plantScanDialog?.open();
          });
        }
        // Hero phone mockup: Lottie (plant scanning animation)
        this.heroPhoneLottie = new HeroPhoneLottie();
        // Hero phone mockup: interactive details expansion
        this.heroPhoneInteractions = new HeroPhoneInteractions();
        // Popular plants: open same dialog with plant details on card tap
        this.plantDetailsRequestHandler = (e: CustomEvent<PlantDetailPayload>) => {
          this.plantScanDialog?.open(e.detail);
        };
        window.addEventListener('plant-details-request', this.plantDetailsRequestHandler as EventListener);

        // Scramble text animation for labels site-wide
        this.scrambleLabelsController = new ScrambleLabelsController();

        // Custom leaf cursor (pointer.png / cursor.png)
        this.customCursor = new CustomCursor();
        this.customCursor.init();

        // Hero content entrance (headline, subhead, CTAs, phone, scroll indicator)
        this.heroContentReveal = new HeroContentReveal();

        // Setup FAQ animation
        this.setupFAQAnimation();

        // GSAP typography animation removed - hero content section removed

        // Setup popular plants carousel
        this.setupPopularPlantsCarousel();

        // Why Florasnap section: Lottie animation from Plant Scanning.json
        this.introLottie = new IntroLottie();
        this.introSectionAnimation = new IntroSectionAnimation();
        this.identifySection3D = new IdentifySection3D();
        this.identifySection3D.init();
        this.identifyImagePreview = new IdentifyImagePreview();

        // Deferred setup: blog scroll, blog detail view, ScrollTrigger refresh (owning_plant section removed)
        setTimeout(() => {
          ScrollTrigger.refresh();
          this.identifySectionReveal = new IdentifySectionReveal();
          this.contactSectionReveal = new ContactSectionReveal();
          this.blogSectionReveal = new BlogSectionReveal();
          this.scrollTriggerResizeHandler = () => ScrollTrigger.refresh();
          window.addEventListener('resize', this.scrollTriggerResizeHandler);

          this.setupBlogHorizontalScroll();
          this.blogDetailView = new BlogDetailView();
          this.blogDetailClickHandler = (e: Event) => {
            const link = (e.target as HTMLElement).closest('.blog-card-link');
            if (!link) return;
            e.preventDefault();
            const article = link.closest('.blog-chapter');
            const chapter = article?.getAttribute('data-chapter');
            if (chapter) this.blogDetailView?.open(parseInt(chapter, 10));
          };
          document.addEventListener('click', this.blogDetailClickHandler);
          ScrollTrigger.refresh();

          this.scrollTriggerLoadHandler = () => {
            ScrollTrigger.refresh();
            if (this.scrollTriggerLoadHandler) {
              window.removeEventListener('load', this.scrollTriggerLoadHandler);
              this.scrollTriggerLoadHandler = null;
            }
          };
          if (document.readyState === 'complete') {
            ScrollTrigger.refresh();
          } else {
            window.addEventListener('load', this.scrollTriggerLoadHandler);
          }
        }, 500);
  }

  // Removed - hero content section removed
  /*
  private setupGetStartedRiveAnimation(): void {
    const getStartedCanvas = document.getElementById('get-started-rive-canvas') as HTMLCanvasElement;
    if (getStartedCanvas) {
      // Set canvas size - even bigger
      const buttonWidth = 900;
      const buttonHeight = 270;
      
      // Set canvas dimensions - Rive will handle the rendering
      getStartedCanvas.width = buttonWidth;
      getStartedCanvas.height = buttonHeight;
      
      // Clear any default canvas background
      const ctx = getStartedCanvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, buttonWidth, buttonHeight);
      }
      
      // Set CSS dimensions - remove all spacing
      getStartedCanvas.style.width = `${buttonWidth}px`;
      getStartedCanvas.style.height = `${buttonHeight}px`;
      getStartedCanvas.style.cursor = 'pointer';
      getStartedCanvas.style.display = 'block';
      getStartedCanvas.style.margin = '0';
      getStartedCanvas.style.padding = '0';
      getStartedCanvas.style.border = '0';
      getStartedCanvas.style.outline = '0';
      getStartedCanvas.style.background = 'transparent';
      getStartedCanvas.style.verticalAlign = 'top';
      getStartedCanvas.style.fontSize = '0';
      getStartedCanvas.style.lineHeight = '0';
      
      console.log('🎬 Initializing Get Started Rive animation button');
      console.log('📐 Canvas size:', getStartedCanvas.width, 'x', getStartedCanvas.height);
      console.log('📐 CSS size:', buttonWidth, 'x', buttonHeight);

      // Initialize Rive animation with get_started.riv
      // Enable autoplay and make it interactive (hover/click)
      this.getStartedRiveAnimation = new RiveAnimation(
        getStartedCanvas,
        this.scrollController,
        '/get_started.riv',
        false, // Disable scroll scrubbing
        true, // Enable autoplay
        false // Use regular scroll callback
      );

      // Add error handling for missing file
      setTimeout(() => {
        if (this.getStartedRiveAnimation && !this.getStartedRiveAnimation.getIsLoaded()) {
          console.error('❌ Get Started Rive animation failed to load!');
          console.error('💡 Check that /get_started.riv exists in the public folder');
          console.error('💡 Check browser console for network errors');
          // Show a visible error on the canvas
          const ctx = getStartedCanvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ff0000';
            ctx.font = 'bold 20px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Rive animation failed to load', buttonWidth / 2, buttonHeight / 2 - 10);
            ctx.font = '14px Arial';
            ctx.fillText('Check console for errors', buttonWidth / 2, buttonHeight / 2 + 15);
          }
        } else {
          console.log('✅ Get Started Rive animation loaded successfully!');
        }
      }, 3000);

      // Make the canvas clickable
      getStartedCanvas.addEventListener('click', () => {
        console.log('🚀 Get Started button clicked!');
        // Add your navigation logic here
        // For example: window.location.href = '/get-started';
      });

      // Handle window resize
      window.addEventListener('resize', () => {
        if (this.getStartedRiveAnimation && getStartedCanvas) {
          const buttonWidth = 300;
          const buttonHeight = 90;
          
          getStartedCanvas.width = buttonWidth;
          getStartedCanvas.height = buttonHeight;
          getStartedCanvas.style.width = `${buttonWidth}px`;
          getStartedCanvas.style.height = `${buttonHeight}px`;
          
          this.getStartedRiveAnimation.resize(buttonWidth, buttonHeight);
        }
      });
    } else {
      console.error('❌ Get Started Rive canvas element not found!');
      console.error('💡 Make sure <canvas id="get-started-rive-canvas"> exists in index.html');
    }
  }
  */

      // setupGSAPTypography removed - hero content section removed

      private setupPopularPlantsCarousel(): void {
        const carouselContainer = document.getElementById('popular-plants-carousel') as HTMLElement;

        if (carouselContainer) {
          this.popularPlantsCarousel = new PopularPlantsCarousel(carouselContainer);
          this.popularPlantsReveal = new PopularPlantsReveal();
        }

        if (!carouselContainer) {
          console.warn('⚠️ Popular plants carousel container not found');
        }
      }

      private setupBlogHorizontalScroll(): void {
        const section = document.getElementById('blog-section') as HTMLElement;
        const pinWrap = document.querySelector('.blog-pin-wrap') as HTMLElement;
        if (section && pinWrap) {
          this.blogHorizontalScroll = new BlogHorizontalScroll(section, pinWrap);
        }
      }

      private setupFAQAnimation(): void {
        // Delay to ensure DOM is ready
        setTimeout(() => {
          this.faqAnimation = new FAQAnimation();
        }, 400);
      }

      private setupFooterAnimation(): void {
        this.footerAnimation = new FooterAnimation();
      }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);

    // Update controls if they exist
    if (this.controls) {
      this.controls.update();
    }

    // Update scene (for animations, etc.)
    this.sceneManager.update();

    // Render the scene
    this.render();
  };

  private render(): void {
    if (this.postProcessing) {
      this.postProcessing.render();
    } else {
      this.renderer.render(
        this.sceneManager.getScene(),
        this.camera.getCamera()
      );
    }
  }

  public dispose(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }

    this.sceneManager.dispose();
    this.renderer.dispose();
    if (this.postProcessing) {
      this.postProcessing.dispose();
    }
        if (this.faqAnimation) {
          this.faqAnimation.dispose();
        }
        if (this.popularPlantsReveal) {
          this.popularPlantsReveal.dispose();
          this.popularPlantsReveal = null;
        }
        if (this.popularPlantsCarousel) {
          this.popularPlantsCarousel.dispose();
        }
        if (this.blogHorizontalScroll) {
          this.blogHorizontalScroll.dispose();
          this.blogHorizontalScroll = null;
        }
        if (this.footerAnimation) {
          this.footerAnimation.dispose();
        }
        if (this.heroNavAnimation) {
          this.heroNavAnimation.dispose();
          this.heroNavAnimation = null;
        }
        if (this.scrambleLabelsController) {
          this.scrambleLabelsController.dispose();
          this.scrambleLabelsController = null;
        }
        if (this.heroBackgroundRotation) {
          this.heroBackgroundRotation.dispose();
          this.heroBackgroundRotation = null;
        }
        if (this.heroLiquidBackground) {
          this.heroLiquidBackground.dispose();
          this.heroLiquidBackground = null;
        }
        if (this.plantScanDialog) {
          this.plantScanDialog.dispose();
          this.plantScanDialog = null;
        }
        if (this.blogDetailClickHandler) {
          document.removeEventListener('click', this.blogDetailClickHandler);
          this.blogDetailClickHandler = null;
        }
        if (this.blogDetailView) {
          this.blogDetailView.dispose();
          this.blogDetailView = null;
        }
        if (this.heroPhoneInteractions) {
          this.heroPhoneInteractions.dispose();
          this.heroPhoneInteractions = null;
        }
        if (this.heroPhoneLottie) {
          this.heroPhoneLottie.dispose();
          this.heroPhoneLottie = null;
        }
        if (this.introLottie) {
          this.introLottie.dispose();
          this.introLottie = null;
        }
        if (this.introSectionAnimation) {
          this.introSectionAnimation.dispose();
          this.introSectionAnimation = null;
        }
        if (this.identifySection3D) {
          this.identifySection3D.dispose();
          this.identifySection3D = null;
        }
        if (this.identifySectionReveal) {
          this.identifySectionReveal.dispose();
          this.identifySectionReveal = null;
        }
        if (this.heroContentReveal) {
          this.heroContentReveal.dispose();
          this.heroContentReveal = null;
        }
        if (this.contactSectionReveal) {
          this.contactSectionReveal.dispose();
          this.contactSectionReveal = null;
        }
        if (this.blogSectionReveal) {
          this.blogSectionReveal.dispose();
          this.blogSectionReveal = null;
        }
        if (this.identifyImagePreview) {
          this.identifyImagePreview.dispose();
          this.identifyImagePreview = null;
        }
        if (this.customCursor) {
          this.customCursor.dispose();
          this.customCursor = null;
        }
        if (this.plantDetailsRequestHandler) {
          window.removeEventListener('plant-details-request', this.plantDetailsRequestHandler as EventListener);
          this.plantDetailsRequestHandler = null;
        }
        if (this.scrollTriggerResizeHandler) {
          window.removeEventListener('resize', this.scrollTriggerResizeHandler);
          this.scrollTriggerResizeHandler = null;
        }
        if (this.scrollTriggerLoadHandler) {
          window.removeEventListener('load', this.scrollTriggerLoadHandler);
          this.scrollTriggerLoadHandler = null;
        }
        this.scrollController.dispose();
        if (this.controls) {
          this.controls.dispose();
        }
      }
}
